import type { StackProps, TypographyProps } from '@mui/material'
import { mergeSx } from 'lib/helpers/mergeSx/mergeSx'

type SlotProps = Omit<StackProps, 'children'> | TypographyProps

interface GetSlotPropsParams<TSlotProps extends SlotProps> {
  slotProps?: TSlotProps
  minHeight: number
}

/**
 * Folds the card's `minHeight` default into a slot's `sx` instead of forwarding it as a
 * system prop. Both halves matter: the caller's `sx` has to survive (a trailing `sx` after
 * the spread would replace it), and the caller's own `minHeight` has to keep winning the
 * way it does today through JSX attribute collapse — once the default lives in `sx` it
 * would otherwise beat the spread. Keyed on presence, not nullishness, because an explicit
 * `null` is a legal responsive value and passes through untouched today.
 */
export const getSlotProps = <TSlotProps extends SlotProps>({
  slotProps,
  minHeight,
}: GetSlotPropsParams<TSlotProps>) => {
  const { sx, minHeight: slotMinHeight, ...rest } = slotProps ?? {}

  return {
    ...rest,
    sx: mergeSx({ minHeight: slotProps && 'minHeight' in slotProps ? slotMinHeight : minHeight }, sx),
  }
}
